import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";

const KINESERVE_LOGO = "https://customer-assets.emergentagent.com/job_kineserve-app/artifacts/7792rvix_KineServe_Logo_Dark.svg";

function LogoWithFallback({ src, alt, className }) {
  const [hasError, setHasError] = useState(false);
  
  if (hasError) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <div className="bg-slate-900 text-white px-3 py-1.5 font-bold text-lg">KS</div>
        <span className="font-bold text-xl text-slate-900">KineServe</span>
      </div>
    );
  }
  
  return (
    <img 
      src={src} 
      alt={alt} 
      className={className}
      onError={() => setHasError(true)}
    />
  );
}

export default function Navbar({ onDemoClick }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { label: "Features", href: "#features" },
    { label: "Platform", href: "#platform" },
    { label: "Contact", href: "#contact" },
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setMobileMenuOpen(false);
  };

  return (
    <>
      <nav 
        className={`navbar ${scrolled ? "navbar-scrolled" : ""}`}
        data-testid="navbar"
      >
        <div className="container-main">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center" data-testid="navbar-logo">
              <LogoWithFallback 
                src={KINESERVE_LOGO} 
                alt="KineServe" 
                className="h-8 md:h-10"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className="text-slate-600 hover:text-slate-900 font-medium transition-colors"
                  data-testid={`nav-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </a>
              ))}
            </div>

            {/* Desktop CTA */}
            <div className="hidden md:flex items-center gap-4">
              <Link 
                to="/admin"
                className="text-slate-600 hover:text-slate-900 font-medium transition-colors"
                data-testid="nav-admin"
              >
                Admin
              </Link>
              <Button 
                onClick={onDemoClick}
                className="bg-slate-900 text-white hover:bg-slate-800 px-6 py-2 font-medium btn-lift"
                data-testid="nav-demo-btn"
              >
                Request Demo
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-nav-toggle"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div 
        className={`mobile-menu ${mobileMenuOpen ? "open" : ""}`}
        data-testid="mobile-menu"
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-8">
            <LogoWithFallback src={KINESERVE_LOGO} alt="KineServe" className="h-8" />
            <button onClick={() => setMobileMenuOpen(false)}>
              <X size={24} />
            </button>
          </div>
          
          <div className="space-y-4">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                className="block py-3 text-lg font-medium text-slate-900 border-b border-slate-100"
              >
                {link.label}
              </a>
            ))}
            <Link
              to="/admin"
              className="block py-3 text-lg font-medium text-slate-900 border-b border-slate-100"
              onClick={() => setMobileMenuOpen(false)}
            >
              Admin Panel
            </Link>
          </div>

          <div className="mt-8">
            <Button 
              onClick={() => {
                setMobileMenuOpen(false);
                onDemoClick();
              }}
              className="w-full bg-slate-900 text-white hover:bg-slate-800 py-4 font-medium"
            >
              Request Demo
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
